import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { getUrl, hasLink, ResourceUri } from '@ngxp/rest';
import { doIfLoadingRequired, isLoadingRequired, StateResource } from '@schir-int-client/ngrx-helpers';
import { VerfahrenResource } from '@schir-int-client/verfahren-shared';
import { isNull, isUndefined } from 'lodash-es';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, share, tap } from 'rxjs/operators';
import {
	createVorgangInVerfahrenAction,
	deleteVorgangAction,
	loadFristenListeAction,
	loadVorgaengeByVerfahrenAction,
	loadVorgaengeInRuecklaufAction,
	loadVorgaengeInWiedervorlageAction,
	loadVorgaengeRechtspflegeAction,
	loadVorgangAction,
	selectVorgangAction,
	setVorgangStatusAction,
	updateDatumsBereichAction,
	updateVorgangNotizAction,
} from './vorgang.actions';
import { VorgangLinkRel } from './vorgang.linkrel';
import { DatumsBereich, VorgangResource, VorgangStatus, VorgangTyp } from './vorgang.model';
import { VorgangState } from './vorgang.reducer';
import {
	datumsBereichSelector,
	isVorgangSelectedSelector,
	vorgaengeByVerfahrenSelector,
	vorgaengeInRuecklaufSelector,
	vorgaengeInWiedervorlageSelector,
	vorgaengeRechtspflegerSelector,
	vorgangSelector,
} from './vorgang.selectors';
import { Moment } from 'moment';

@Injectable()
export class VorgangFacade {
	constructor(private readonly store: Store<VorgangState>) {}

	vorgang: Observable<VorgangResource> = this.store
		.select(vorgangSelector)
		.pipe(map(stateResource => stateResource?.resource));

	vorgaengeRechtspfleger: Observable<VorgangResource[]> = this.store.select(vorgaengeRechtspflegerSelector).pipe(
		tap(vorgaengeRechtspfleger => {
			if (isNull(vorgaengeRechtspfleger)) {
				this.store.dispatch(loadVorgaengeRechtspflegeAction());
			}
		}),
		share(),
		map(vorgaengeRechtspfleger => {
			return vorgaengeRechtspfleger ? vorgaengeRechtspfleger : [];
		})
	);

	vorgaengeInRuecklauf: Observable<VorgangResource[]> = this.store.select(vorgaengeInRuecklaufSelector).pipe(
		tap(vorgaenge => {
			if (isNull(vorgaenge)) {
				this.store.dispatch(loadVorgaengeInRuecklaufAction());
			}
		}),
		share(),
		map(vorgaenge => {
			return vorgaenge ? vorgaenge : [];
		})
	);

	getVorgaengeByVerfahren(verfahren: VerfahrenResource): Observable<VorgangResource[]> {
		return this.store.select(vorgaengeByVerfahrenSelector(getUrl(verfahren))).pipe(
			map(vorgaenge => {
				if (isUndefined(vorgaenge)) {
					this.store.dispatch(loadVorgaengeByVerfahrenAction({ verfahren }));
					return [];
				}
				return vorgaenge;
			})
		);
	}

	loadVorgang(vorgangUri: ResourceUri) {
		this.store.dispatch(loadVorgangAction({ vorgangUri }));
	}

	getSelectedVorgang(): Observable<VorgangResource> {
		return this.store.select(vorgangSelector).pipe(
			filter(stateResource => !isLoadingRequired(stateResource)),
			map(stateResource => stateResource.resource)
		);
	}

	loadVorgaengeRechtspfleger() {
		this.store.dispatch(loadVorgaengeRechtspflegeAction());
	}

	loadVorgaengeInRuecklauf() {
		this.store.dispatch(loadVorgaengeInRuecklaufAction());
	}

	createVorgangInVerfahren(vorgangTyp: VorgangTyp, uri: ResourceUri) {
		this.store.dispatch(createVorgangInVerfahrenAction({ vorgangTyp, uri }));
	}

	getVorgaengeInRuecklauf(): Observable<VorgangResource[]> {
		return this.store.select(vorgaengeInRuecklaufSelector).pipe(
			tap(vorgaenge => {
				if (isNull(vorgaenge)) {
					this.store.dispatch(loadVorgaengeInRuecklaufAction());
				}
			})
		);
	}

	getVorgaengeInWiedervorlage(von: Moment, bis: Moment): Observable<StateResource<VorgangResource[]>> {
		return this.store.select(vorgaengeInWiedervorlageSelector).pipe(
			filter(
				vorgaenge =>
					!doIfLoadingRequired(vorgaenge, () =>
						this.store.dispatch(
							loadVorgaengeInWiedervorlageAction({
								von,
								bis,
							})
						)
					)
			)
		);
	}

	refreshWiedervorlagen(von: Moment, bis: Moment) {
		this.store.dispatch(loadVorgaengeInWiedervorlageAction({ von, bis }));
		this.store.dispatch(updateDatumsBereichAction({ von, bis }));
	}

	getDatumsBereich(): Observable<DatumsBereich> {
		return this.store.select(datumsBereichSelector);
	}

	loadFristenListe() {
		this.store.dispatch(loadFristenListeAction());
	}

	isSelectedVorgang(vorgangResource: VorgangResource): Observable<boolean> {
		return this.store.select(isVorgangSelectedSelector(vorgangResource));
	}

	setSelectedVorgang(vorgang: VorgangResource) {
		this.store.dispatch(selectVorgangAction({ vorgang }));
	}

	updateNotiz(notiz: string, vorgang: VorgangResource) {
		this.store.dispatch(updateVorgangNotizAction({ notiz, vorgang }));
	}

	setVorgangStatus(
		vorgang: VorgangResource,
		status: Exclude<VorgangStatus, VorgangStatus.NEU>,
		wiedervorlageDatum: Moment
	) {
		this.store.dispatch(setVorgangStatusAction({ vorgang, status, wiedervorlageDatum }));
	}

	deleteVorgang(vorgang: VorgangResource) {
		this.store.dispatch(deleteVorgangAction({ vorgang }));
	}

	/**
	 * Dient der Ausblendung des Vorgang-Löschen-Buttons, ohne den Vorgang und damit alle abhängigen Elemente neu laden zu müssen.
	 * Neuladen des aktuellen Vorgangs würde ein Flackern der Seite bewirken.
	 * Wird ein Vorgang neu geladen, ist der deletable-Status bereits vom Server bestimmt. Zusätzlich kann ein Vorgang nachträglich z.B.
	 * durch Anlegen einer Verfügung unlöschbar werden. Daher ist die hier verwendete Info nur bis zum nächsten Laden der Vorgänge
	 * relevant.
	 *
	 * TODO: prüfen, ob es nicht reicht, wenn der Vorgang über ein Observable weiß, ob es zu ihm Verfuegungen im Store gibt?
	 * @param vorgang
	 */
	private undeletablesSubject: BehaviorSubject<Set<string>> = new BehaviorSubject<Set<string>>(new Set<string>());
	undeletables$: Observable<Set<string>> = this.undeletablesSubject.asObservable();

	isDeletable(vorgang: VorgangResource): Observable<boolean> {
		return this.undeletables$.pipe(
			map(
				undeleteables =>
					hasLink(vorgang, VorgangLinkRel.DELETE) && !undeleteables.has(vorgang._links['self'].href)
			)
		);
	}

	registerUndeletable(vorgang: VorgangResource): void {
		const currentSet = this.undeletablesSubject.getValue();
		currentSet.add(vorgang._links['self'].href);
		this.undeletablesSubject.next(currentSet);
	}

	registerDeletable(vorgangId: string): void {
		const currentSet = this.undeletablesSubject.getValue();
		const entryToDelete = Array.from(currentSet).find(entry => entry.endsWith('/' + vorgangId));
		if (entryToDelete) {
			currentSet.delete(entryToDelete);
			this.undeletablesSubject.next(currentSet);
		}
	}
}
